import { useCallback } from 'react';

import { useToast } from '@/components/ui/toast';

/**
 * Standard error structure that can come from various sources
 */
interface ApiError {
  response?: {
    data?: {
      message?: string;
      error?: string;
    };
    status?: number;
    statusText?: string;
  };
  message?: string;
  code?: string;
}

/**
 * Error handler options
 */
interface ErrorHandlerOptions {
  /** Custom error message to display instead of the API message */
  customMessage?: string;
  /** Duration to show the error toast (default 7000ms) */
  duration?: number;
  /** Whether to log the error to console (default true in dev) */
  logError?: boolean;
  /** Custom status code handlers */
  statusHandlers?: Record<number, string>;
}

/**
 * Default error messages by status code
 */
const DEFAULT_STATUS_MESSAGES: Record<number, string> = {
  400: 'Invalid request. Please check your input.',
  401: 'You are not authenticated. Please log in.',
  403: 'You do not have permission to perform this action.',
  404: 'The requested resource was not found.',
  409: 'A conflict occurred. The resource may have been modified.',
  422: 'Validation failed. Please check your input.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again later.',
  502: 'Bad gateway. The server is temporarily unavailable.',
  503: 'Service unavailable. Please try again later.',
  504: 'Gateway timeout. The request took too long.',
};

/**
 * Extract error message from various error formats
 */
function extractErrorMessage(
  error: unknown,
  options?: ErrorHandlerOptions,
): string {
  // Use custom message if provided
  if (options?.customMessage) {
    return options.customMessage;
  }

  const apiError = error as ApiError;

  // Get status code
  const statusCode = apiError.response?.status;

  // Check for custom status handler
  if (statusCode && options?.statusHandlers?.[statusCode]) {
    return options.statusHandlers[statusCode];
  }

  // Check for default status message
  if (statusCode && DEFAULT_STATUS_MESSAGES[statusCode]) {
    return DEFAULT_STATUS_MESSAGES[statusCode];
  }

  // Extract message from API response
  const apiMessage =
    apiError.response?.data?.message ??
    apiError.response?.data?.error ??
    apiError.response?.statusText;

  if (apiMessage) {
    return apiMessage;
  }

  // Extract message from error object
  if (apiError.message) {
    return apiError.message;
  }

  // Check for network errors
  if (!statusCode) {
    return 'Network error. Please check your connection and try again.';
  }

  // Generic fallback
  return 'An unexpected error occurred. Please try again.';
}

export const useErrorHandler = () => {
  const { showToast } = useToast();

  /**
   * Handle error and display appropriate toast notification
   * Returns an error handler function that can be passed to onError callbacks
   */
  const handleError = useCallback(
    (options?: ErrorHandlerOptions) => {
      return (error: unknown) => {
        const message = extractErrorMessage(error, options);
        const duration = options?.duration ?? 7000;

        // Log error in development
        if (
          (options?.logError ?? import.meta.env.DEV) &&
          import.meta.env.DEV
        ) {
          console.error('Error caught by error handler:', error);
        }

        showToast(message, 'error', duration);
      };
    },
    [showToast],
  );

  /**
   * Handle success and display success toast notification
   */
  const handleSuccess = useCallback(
    (message: string, duration = 5000) => {
      showToast(message, 'success', duration);
    },
    [showToast],
  );

  /**
   * Handle info notification
   */
  const handleInfo = useCallback(
    (message: string, duration = 5000) => {
      showToast(message, 'info', duration);
    },
    [showToast],
  );

  return {
    handleError,
    handleSuccess,
    handleInfo,
  };
};

/**
 * Type for error handler function returned by useErrorHandler
 */
export type ErrorHandler = ReturnType<typeof useErrorHandler>['handleError'];
